import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Save, CreditCard, Link2, RefreshCw, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const WEBHOOK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/sumup`;

export default function SumUpSettingsPage({ embedded = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState({
    enabled: false, sandbox: false, merchant_code: '', affiliate_app_id: '',
    api_key_set: false, api_key_masked: '', affiliate_key_set: false,
    webhook_secret_set: false, configured: false,
  });
  const [secrets, setSecrets] = useState({ api_key: '', affiliate_key: '', webhook_secret: '' });
  const [provider, setProvider] = useState('none');
  const [savingProvider, setSavingProvider] = useState(false);
  const [studios, setStudios] = useState([]);
  const [readers, setReaders] = useState([]);
  const [pair, setPair] = useState({ pairing_code: '', name: 'Empfang Solo', studio_id: '' });
  const [pairing, setPairing] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const load = async () => {
    try {
      const [c, st] = await Promise.all([
        axios.get(`${API}/settings/sumup`, authHeaders()),
        axios.get(`${API}/studios`, authHeaders()).catch(() => ({ data: [] })),
      ]);
      setCfg(c.data);
      try {
        const ct = await axios.get(`${API}/settings/card-terminal`, authHeaders());
        setProvider(ct.data.provider || 'none');
      } catch (e) { /* ignore */ }
      const list = Array.isArray(st.data) ? st.data : (st.data?.studios || st.data?.items || []);
      setStudios(list);
      if (c.data.configured) loadReaders();
    } catch (e) {
      toast.error('Fehler beim Laden der SumUp-Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const loadReaders = async () => {
    try {
      const r = await axios.get(`${API}/settings/sumup/readers`, authHeaders());
      setReaders(r.data.readers || []);
    } catch (e) { /* ignore */ }
  };

  const saveProvider = async (v) => {
    setProvider(v);
    setSavingProvider(true);
    try {
      await axios.put(`${API}/settings/card-terminal`, { provider: v }, authHeaders());
      const labels = { none: 'Kein Kartenterminal', sumup: 'SumUp Solo', worldline: 'Worldline Tap on Mobile' };
      toast.success(`Aktiviert: ${labels[v]}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Umschalten');
    } finally {
      setSavingProvider(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        sandbox: cfg.sandbox,
        merchant_code: cfg.merchant_code, affiliate_app_id: cfg.affiliate_app_id,
      };
      if (secrets.api_key.trim()) payload.api_key = secrets.api_key.trim();
      if (secrets.affiliate_key.trim()) payload.affiliate_key = secrets.affiliate_key.trim();
      if (secrets.webhook_secret.trim()) payload.webhook_secret = secrets.webhook_secret.trim();
      await axios.put(`${API}/settings/sumup`, payload, authHeaders());
      toast.success('SumUp-Einstellungen gespeichert');
      setSecrets({ api_key: '', affiliate_key: '', webhook_secret: '' });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const doPair = async () => {
    if (!pair.pairing_code.trim()) { toast.error('Bitte den Kopplungscode vom Solo eingeben'); return; }
    setPairing(true);
    try {
      await axios.post(`${API}/settings/sumup/pair-reader`, pair, authHeaders());
      toast.success('Kartenleser gekoppelt ✅');
      setPair({ ...pair, pairing_code: '' });
      loadReaders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kopplung fehlgeschlagen');
    } finally {
      setPairing(false);
    }
  };

  if (loading) return <div className="p-8 text-gray-500">Lädt...</div>;

  return (
    <div className="p-8 max-w-4xl">
      {!embedded && (
        <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate('/settings')} data-testid="sumup-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Zurück zu Einstellungen
        </Button>
      )}

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CreditCard className="h-6 w-6 text-purple-600" />
          Kartenterminal
        </h1>
        <p className="text-gray-600 mt-1">
          Wähle, über welches Terminal Kartenzahlungen abgewickelt werden:
          <b> SumUp Solo</b> (Cloud, eigenes Terminal) oder <b>Worldline Tap on Mobile</b>
          (direkt auf dem SUNMI-Gerät).
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Kartenterminal auswählen</CardTitle>
          <CardDescription>
            Es kann immer nur ein Kartenterminal aktiv sein. In der Admin-App erscheint
            nur der Kassier-Button des gewählten Anbieters.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-md">
            <Select value={provider} onValueChange={saveProvider} disabled={savingProvider}>
              <SelectTrigger data-testid="card-terminal-provider-select">
                <SelectValue placeholder="Kartenterminal wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none" data-testid="card-terminal-none">Aus (kein Kartenterminal)</SelectItem>
                <SelectItem value="sumup" data-testid="card-terminal-sumup">SumUp Solo</SelectItem>
                <SelectItem value="worldline" data-testid="card-terminal-worldline">Worldline Tap on Mobile (SUNMI)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {provider === 'worldline' && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="worldline-hint">
              <b>Worldline aktiv.</b> Läuft direkt auf dem SUNMI-Gerät (die Admin-App muss dort
              installiert sein). Voraussetzung: Worldline muss den App-Paketnamen
              <code className="mx-1">ch.strongfit.admin</code> für WPI freischalten.
            </div>
          )}
          {provider === 'sumup' && !cfg.configured && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
              SumUp ist aktiviert, aber noch nicht konfiguriert. Bitte unten API Key + Merchant Code speichern.
            </div>
          )}
        </CardContent>
      </Card>

      {provider === 'sumup' && (<>
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">SumUp: Zugangsdaten</CardTitle>
          <CardDescription>Werden sicher nur im Backend gespeichert. Secrets werden maskiert angezeigt.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch checked={!!cfg.sandbox} onCheckedChange={(v) => setCfg({ ...cfg, sandbox: v })} data-testid="sumup-sandbox-switch" />
            <span className="text-sm">Sandbox / Testmodus</span>
          </div>

          <div>
            <label className="text-xs text-gray-500">API Key {cfg.api_key_set && <span className="text-green-600">(gesetzt: {cfg.api_key_masked})</span>}</label>
            <Input type="password" autoComplete="off" placeholder={cfg.api_key_set ? 'unverändert lassen…' : 'sup_sk_…'}
              value={secrets.api_key} onChange={(e) => setSecrets({ ...secrets, api_key: e.target.value })} data-testid="sumup-api-key-input" />
          </div>
          <div>
            <label className="text-xs text-gray-500">Merchant Code</label>
            <Input placeholder="z.B. MABCDEFG" value={cfg.merchant_code}
              onChange={(e) => setCfg({ ...cfg, merchant_code: e.target.value })} data-testid="sumup-merchant-code-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500">Affiliate Key {cfg.affiliate_key_set && <span className="text-green-600">(gesetzt)</span>}</label>
              <Input type="password" autoComplete="off" placeholder={cfg.affiliate_key_set ? 'unverändert lassen…' : 'affiliate key'}
                value={secrets.affiliate_key} onChange={(e) => setSecrets({ ...secrets, affiliate_key: e.target.value })} data-testid="sumup-affiliate-key-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Affiliate App-ID</label>
              <Input placeholder="z.B. ch.strongfit.crm" value={cfg.affiliate_app_id}
                onChange={(e) => setCfg({ ...cfg, affiliate_app_id: e.target.value })} data-testid="sumup-affiliate-app-input" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500">Webhook Secret (optional) {cfg.webhook_secret_set && <span className="text-green-600">(gesetzt)</span>}</label>
            <Input type="password" autoComplete="off" placeholder={cfg.webhook_secret_set ? 'unverändert lassen…' : 'optional'}
              value={secrets.webhook_secret} onChange={(e) => setSecrets({ ...secrets, webhook_secret: e.target.value })} data-testid="sumup-webhook-secret-input" />
          </div>

          <div className="rounded-lg bg-gray-50 border p-3 text-xs text-gray-600">
            <div className="font-medium text-gray-700 mb-1">Webhook-URL für SumUp:</div>
            <code className="break-all text-purple-700" data-testid="sumup-webhook-url">{WEBHOOK_URL}</code>
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="sumup-save-btn">
              <Save className="h-4 w-4 mr-1" /> {saving ? 'Speichern…' : 'Speichern'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Link2 className="h-4 w-4" /> Kartenleser koppeln</CardTitle>
          <CardDescription>
            Am Solo: Menü → <i>Connections → API → Connect</i>, dann den 8-stelligen Code hier eingeben (läuft nach ~5 Min ab).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!cfg.configured && (
            <p className="text-sm text-amber-600">Bitte zuerst API Key + Merchant Code speichern, dann kannst du einen Leser koppeln.</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500">Kopplungscode</label>
              <Input placeholder="z.B. 4WLFDSBF" value={pair.pairing_code}
                onChange={(e) => setPair({ ...pair, pairing_code: e.target.value.toUpperCase() })} data-testid="sumup-pairing-code-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Name</label>
              <Input value={pair.name} onChange={(e) => setPair({ ...pair, name: e.target.value })} data-testid="sumup-reader-name-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Studio</label>
              <Select value={pair.studio_id} onValueChange={(v) => setPair({ ...pair, studio_id: v })}>
                <SelectTrigger data-testid="sumup-reader-studio-select"><SelectValue placeholder="Studio wählen" /></SelectTrigger>
                <SelectContent>
                  {studios.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={loadReaders} disabled={!cfg.configured} data-testid="sumup-refresh-readers-btn">
              <RefreshCw className="h-4 w-4 mr-1" /> Leser aktualisieren
            </Button>
            <Button onClick={doPair} disabled={pairing || !cfg.configured} data-testid="sumup-pair-btn">
              <Link2 className="h-4 w-4 mr-1" /> {pairing ? 'Koppeln…' : 'Koppeln'}
            </Button>
          </div>

          {readers.length > 0 && (
            <div className="space-y-2 pt-2">
              <div className="text-xs font-medium text-gray-500">Gekoppelte Leser</div>
              {readers.map((r) => (
                <div key={r.id} className="flex items-center justify-between border rounded-lg p-2 text-sm bg-gray-50/50" data-testid={`sumup-reader-${r.id}`}>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="font-medium">{r.name || r.id}</span>
                    <span className="text-gray-400 text-xs">{r.status || ''}</span>
                  </div>
                  <span className="text-xs text-gray-500">{r.studio_name || 'kein Studio'}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </>)}
    </div>
  );
}
